/**
 * Persist cloned voices in IndexedDB via localforage.
 */

import localforage from 'localforage';

const STORE_NAME = 'l123-tts-cloned-voices';
const KEY = 'voices';

export type ClonedVoice = {
  id: string;
  name: string;
  /** Engine that produced the embedding, e.g. 'chatterbox' | 'vieneu' */
  engine: string;
  modelId: string;
  /** ISO language hint */
  lang: string;
  sampleRate: number;
  /** Speaker embedding (Float32) — may be large */
  embedding: Float32Array;
  /** Optional short reference PCM for preview (downsampled) */
  previewPcm?: Float32Array;
  /** Full reference PCM for re-synth (persisted) */
  metaRefPcm?: Float32Array;
  /** Engine-specific meta (VieNeu refCodes, etc.) */
  engineMeta?: any;
  createdAt: number;
};

let store: any = null;

function getStore() {
  if (store) return store;
  store = localforage.createInstance({
    name: STORE_NAME,
    storeName: 'voices',
    description: 'Language123 TTS cloned voices',
  });
  return store;
}

function serializeVoice(v: ClonedVoice): any {
  return {
    id: v.id,
    name: v.name,
    engine: v.engine,
    modelId: v.modelId,
    lang: v.lang,
    sampleRate: v.sampleRate,
    embedding: Array.from(v.embedding),
    previewPcm: v.previewPcm ? Array.from(v.previewPcm) : null,
    metaRefPcm: v.metaRefPcm ? Array.from(v.metaRefPcm) : null,
    engineMeta: v.engineMeta || null,
    createdAt: v.createdAt,
  };
}

function deserializeVoice(raw: any): ClonedVoice | null {
  if (!raw || !raw.id || !raw.embedding) return null;
  return {
    id: String(raw.id),
    name: String(raw.name || 'Voice'),
    engine: String(raw.engine || ''),
    modelId: String(raw.modelId || ''),
    lang: String(raw.lang || 'en'),
    sampleRate: Number(raw.sampleRate) || 24000,
    embedding: new Float32Array(raw.embedding),
    previewPcm: raw.previewPcm ? new Float32Array(raw.previewPcm) : undefined,
    metaRefPcm: raw.metaRefPcm ? new Float32Array(raw.metaRefPcm) : undefined,
    engineMeta: raw.engineMeta || undefined,
    createdAt: Number(raw.createdAt) || Date.now(),
  };
}

export async function listClonedVoices(): Promise<ClonedVoice[]> {
  try {
    const raw = await getStore().getItem(KEY);
    if (!raw || !Array.isArray(raw)) return [];
    const out: ClonedVoice[] = [];
    raw.forEach((item: any) => {
      const v = deserializeVoice(item);
      if (v) out.push(v);
    });
    return out.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

export async function saveClonedVoice(voice: ClonedVoice): Promise<void> {
  const list = await listClonedVoices();
  const filtered = list.filter((v) => v.id !== voice.id);
  filtered.unshift(voice);
  // Cap at 20 voices to avoid IndexedDB bloat
  const capped = filtered.slice(0, 20);
  await getStore().setItem(KEY, capped.map(serializeVoice));
}

export async function deleteClonedVoice(id: string): Promise<void> {
  const list = await listClonedVoices();
  const next = list.filter((v) => v.id !== id);
  await getStore().setItem(KEY, next.map(serializeVoice));
}

export async function clearClonedVoices(): Promise<void> {
  await getStore().setItem(KEY, []);
}

export function newVoiceId(): string {
  return 'cv_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}
